import { Box, Col, Paginator, Radio, RadioGroup, Row, Search, SearchResult, TextInput, } from '@digitools/honest-react';
import LfgBaseLogo from 'src/assets/lfg_base_logo.svg';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import useTealium from '../../../hooks/useTealium';
import RemoveUserModal, { REMOVE_EMPLOYER_ACCESS_LABEL } from './RemoveUserModal';
import useAuthentication from 'src/hooks/useAuthentication';
import { Link } from 'react-router-dom';
import { isLfgMlpUser } from 'src/utils/userUtils';
import { formatRoleNames, isEmployerAdmin } from 'src/utils/userRoleUtils';
const StyledCustomerContainer = styled('div') `
  margin-top: 0;
  margin-bottom: 0;
`;
// Employer Admin names render as plain text (not a Link); use the theme's black so it
// survives theme/rebrand changes rather than a hardcoded hex (CX-3873 review S4).
const StyledAdminName = styled('span') `
  color: ${({ theme }) => theme.palette.black};
`;
const StyledTitle = styled('div') `
  padding: 0 16px;
  font-weight: 900;
  margin-bottom: 1rem;
`;
const NoResults = styled('div') `
  padding: 0 16px;
  color: rgb(126, 134, 153);
`;
const StyledPaginatorContainer = styled('div') `
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  fieldset {
      width: 100%;
  }
`;
const StyledHeaderRow = styled('div') `
  font-weight: bold;
`;
const StyledBox = styled(Box) `
  margin-top: 0;
  padding:0;
  margin-bottom: 0;
  border-radius: 0;
  border: none;
  align-self: stretch;
  &:nth-child(even) {
    background-color: rgb(244, 243, 244);
  }
  .user-search-list>button{
    border: none;
    background-color: transparent;
    height: 100%;
    padding: 0;
    margin: 0;
  }
`;
const ANNOUNCE_MESSAGE_TEMPLATE = (searchString, filteredList) => searchString
    ? `${filteredList.length} user${filteredList.length !== 1 ? 's' : ''} found when filtering by ${searchString}`
    : `Filter cleared, showing all ${filteredList.length} user${filteredList.length !== 1 ? 's' : ''}`;
const EmptyResults = () => (<SearchResult>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span>No Results!</span>
    </div>
  </SearchResult>);
const PagedUsers = React.memo(({ pagedData, handleSelectUser, removeUser, setFilteredUserList, setSearchString, user, uniqueId }) => {
    return (<>
      {pagedData.map((item, idx) => {
            const employerAdmin = isEmployerAdmin(item.roleNames);
            return (<StyledBox key={idx} data-testid={`user-${idx}`}>
            <Row style={{ padding: '1rem' }}>
              <Col md={4} lg={4}>
                {employerAdmin ? (<StyledAdminName data-testid={`user-${idx}-name`}>
                    {`${item.lastName}, ${item.firstName} (${item.email})`}
                  </StyledAdminName>) : (<Link to={'/admin/permissions'} style={{ justifyContent: 'start' }} onClick={() => {
                        handleSelectUser(item);
                        setFilteredUserList([]);
                        setSearchString('');
                    }} data-testid={`user-${idx}-link`}>
                    {`${item.lastName}, ${item.firstName} (${item.email})`}
                  </Link>)}
              </Col>
              <Col md={3} lg={3} aria-describedby={`role-header-${uniqueId}`} data-testid={`user-${idx}-role`}>
                {formatRoleNames(item.roleNames)}
              </Col>
              <Col md={2} lg={2} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {!item.lastLogin || item.lastLogin.startsWith('1900-')
                    ? 'Never'
                    : formatInTimeZone(parseISO(item.lastLogin), 'America/New_York', 'MM/dd/yyyy')}
                  </span>
                {isLfgMlpUser(item.userId) && (<LfgBaseLogo aria-labelledby={`lfg-logo-${uniqueId}`} style={{ marginLeft: '0.7rem' }}/>)}
              </Col>
              <Col md={3} lg={3} style={{ display: 'inline' }}>
                {employerAdmin ? (
                // Employer Admin rows have no Remove action. Reserve the same vertical
                // footprint as the Remove button (same wrapped label, shared constant so it
                // can't drift) so these rows match the height of interactive rows.
                // Hidden from view and assistive technology.
                <span aria-hidden={'true'} data-testid={`user-${idx}-remove-spacer`} style={{
                        visibility: 'hidden',
                        display: 'block',
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        lineHeight: 'var(--mantine-line-height)',
                    }}>
                    <b>
                      <span style={{ display: 'inline-block', width: '0.75rem' }}/> {REMOVE_EMPLOYER_ACCESS_LABEL}
                    </b>
                  </span>) : (removeUser && user && !user?.ssoUser && (<RemoveUserModal removeSelectUser={removeUser} user={item}/>))}
              </Col>
            </Row>
          </StyledBox>);
        })}
    </>);
});
const UserSearch = (props) => {
    const { trackEvent } = useTealium();
    const [filteredUserList, setFilteredUserList] = useState(props.users);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchString, setSearchString] = useState('');
    const [announceMessage, setAnnounceMessage] = useState('');
    const isInitialRender = useRef(true);
    const { user } = useAuthentication();
    const generatedId = useId();
    const uniqueId = props.uniqueId || generatedId;
    const hasLfgMlpUsers = props.users.some(user => isLfgMlpUser(user.userId));
    useEffect(() => {
        setFilteredUserList(props.users);
    }, [props.showAllIndicator]);
    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        const timeout = setTimeout(() => {
            setAnnounceMessage(ANNOUNCE_MESSAGE_TEMPLATE(searchString, filteredUserList));
        }, 400);
        return () => clearTimeout(timeout);
    }, [filteredUserList.length, searchString]);
    const filterResultsCustomSearch = (query) => {
        const searchString = query.target.value;
        const filteredList = props.users.filter((user) => {
            if (searchString) {
                setSearchString(searchString);
                setCurrentPage(0);
                const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
                const searchData = `${fullName} (${user.email})`.toLowerCase();
                return searchData.includes(query.target.value.toLowerCase());
            }
            else {
                setSearchString('');
                return props.users;
            }
        });
        if (filteredList.length === 0) {
            trackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.CHANGE,
                event_type: TEALIUM_EVENT.EVENT_TYPE.ADMIN_USER_SEARCH,
                event_name: 'No user accounts matched',
            });
        }
        setFilteredUserList(filteredList);
        return filteredList;
    };
    const filterResults = async (query) => {
        if (query) {
            const filteredResults = props.users.filter((user) => {
                const userData = `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase();
                return userData.includes(query);
            });
            setAnnounceMessage(`${filteredResults.length} user${filteredResults.length !== 1 ? 's' : ''} found when filtering by ${query}`);
            return filteredResults;
        }
        setAnnounceMessage('');
        return Promise.resolve([]);
    };
    const Result = ({ result }) => {
        return (<SearchResult key={`SearchResultItem-${result.userId}`} onClick={() => handleUser(result)}>
        <span>{`${result.firstName} ${result.lastName} (${result.email})`}</span>
      </SearchResult>);
    };
    const PagedUsersCopy = ({ pagedData }) => {
        return (<>
        <RadioGroup label={' '} field={'userId'}>
          <div role={'list'}>
            {pagedData.map((item, idx) => {
                return (<>
                  {props.selectedUser !== undefined && props.selectedUser.userId !== item.userId && (<>
                      <StyledBox key={idx} data-testid={`user-${idx}`} role={'listitem'}>
                        <Row>
                          <Col md={7} lg={7} style={{ padding: '0.5rem' }}>
                            <Radio labelStyle={{ display: 'block' }} name={'userId'} id={item.userId} label={<span>
                                  <span style={{ display: 'block' }}>{`${item.lastName}, ${item.firstName}`}</span>
                                  <span style={{ display: 'block' }}>{item.email}</span>
                                </span>} value={item}/>{' '}
                          </Col>
                          <Col md={5} lg={5} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ whiteSpace: 'nowrap' }}>
                              {!item.lastLogin || item.lastLogin.startsWith('1900-')
                            ? 'Never'
                            : formatInTimeZone(parseISO(item.lastLogin), 'America/New_York', 'MM/dd/yyyy')}
                            </span>
                            {isLfgMlpUser(item.userId) && (<LfgBaseLogo aria-labelledby={`lfg-logo-${uniqueId}`} style={{ marginLeft: '0.7rem' }}/>)}
                          </Col>
                        </Row>
                      </StyledBox>
                    </>)}
                </>);
            })}
          </div>
        </RadioGroup>
      </>);
    };
    const handleUser = (item) => {
        if (item !== undefined) {
            props.handleSelectUser(item);
            setFilteredUserList(props.users);
            setSearchString('');
        }
    };
    const handleShowAll = () => {
        setFilteredUserList(props.users);
        setSearchString('');
        props.showAllUsers();
    };
    const renderPagedUsers = useCallback(({ pagedData }) => (<PagedUsers pagedData={pagedData} handleSelectUser={props.handleSelectUser} removeUser={props.removeUser} setFilteredUserList={setFilteredUserList} setSearchString={setSearchString} user={user} uniqueId={uniqueId}/>), [props.handleSelectUser, props.removeUser, user, uniqueId]);
    return (<>
      {props.showAllIndicator ? (<>
          {props.users.length === 0 ? (<p data-testid='NoUsersFound' aria-labelledby='NoUsersFound' data-public={true}>
              No Users Found
            </p>) : (<>
              <TextInput field={'user-name'} label={'user-name'} role={'textbox'} hideLabel={true} placeholder={'search by user name'} onChange={filterResultsCustomSearch} icon={'search'} aria-label='User Name' data-testid={'search-input'} data-public={true}/>
              <div aria-live='assertive' aria-atomic='true' className={'sr-only'}>
                {announceMessage}
              </div>
              <StyledCustomerContainer id={'user-list'} aria-label={'User List'} data-testid={'user-list'} data-public={true}>
                {props.showCopyPermissionsTable ? null : (<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', marginBottom: '1rem' }}>
                    <StyledTitle style={{ padding: 0, marginBottom: 0 }} id='AllUsers' aria-labelledby='AllUsers'>
                      {'All Users'}
                    </StyledTitle>
                    {hasLfgMlpUsers && <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <LfgBaseLogo aria-labelledby={`lfg-logo-${uniqueId}`}/>
                      <span style={{ fontFamily: 'Roboto', fontSize: '1rem', fontStyle: 'italic', fontWeight: 400, lineHeight: '1.875rem' }} id={`lfg-logo-${uniqueId}`}>{'Indicates user logs in from lincolnfinancial.com'}</span>
                    </div>}
                  </div>)}

                {filteredUserList.length === 0 ? (<NoResults data-testid={'NoResults'} id='NoResults' aria-labelledby='NoResults'>
                    {'No Results Found'}
                  </NoResults>) : (<StyledPaginatorContainer>
                    <StyledHeaderRow>
                      {props.showCopyPermissionsTable ? (<Row>
                          <Col md={7} lg={7}></Col>
                          <Col md={5} lg={5} style={{ whiteSpace: 'nowrap' }}>Last logged in</Col>
                        </Row>) : (<Row>
                          <Col md={4} lg={4}></Col>
                          <Col md={3} lg={3} id={`role-header-${uniqueId}`}>Role</Col>
                          <Col md={2} lg={2} style={{ whiteSpace: 'nowrap' }}>Last logged in</Col>
                          <Col md={3} lg={3}></Col>
                        </Row>)}
                    </StyledHeaderRow>
                    <Paginator ariaDropdownLabel={'User Access Table'} data={filteredUserList} renderContent={props.showCopyPermissionsTable
                        ? PagedUsersCopy
                        : renderPagedUsers} managed={false} rowsPerPage={rowsPerPage} currentPage={currentPage} setRowsPerPage={(selected) => setRowsPerPage(selected)} setCurrentPage={setCurrentPage}/>
                  </StyledPaginatorContainer>)}
              </StyledCustomerContainer>
            </>)}
        </>) : (<Box color={'grayLightest'} style={{ marginBottom: '2rem', marginTop: '2rem', zIndex: 2 }} data-public={true}>
          <Row style={{ marginBottom: '5px' }}>
            <Col sm={6} md={6} lg={6} id='SearchUsers' aria-labelledby='SearchUsers'>
              <strong>Search Users</strong>
            </Col>
            <Col sm={6} md={6} lg={6}>
              <Link to={'/admin/permissions'} style={{ display: 'flex', justifyContent: 'flex-end' }} aria-label='View All Users' onClick={handleShowAll}>
                View All Users
              </Link>
            </Col>
          </Row>
          <Search label='User Search' field='' role={'searchbox'} emptyResultComponent={EmptyResults} resultComponent={({ result }) => <Result result={result}/>} placeholder={'Enter User Name'} data-testid={'search-input-new'} aria-label='User Search' onQueryChange={filterResults}/>
          {searchString && filteredUserList.length === 0 ? <NoResults>{'No Results Found'}</NoResults> : <></>}
          <div aria-live='assertive' aria-atomic='true' className={'sr-only'}>
            {announceMessage}
          </div>
        </Box>)}
    </>);
};
export default UserSearch;
